import React, { useEffect, useMemo, useRef } from 'react';

// Deterministic pseudo-random for the base layout, so the field is stable between renders.
const rand = (seed) => {
  const x = Math.sin(seed * 9301 + 49297) * 233280;
  return x - Math.floor(x);
};

/**
 * Decorative bar-matrix graphic for the hero. Pure CSS/DOM, no canvas.
 * Brighter toward the left-centre columns, fading out to the edges.
 * Every cell flickers on its own rhythm, and random cells briefly "spark"
 * white; which cells spark changes every time, so the pattern never repeats.
 */
export default function HeroMatrix({ rows = 30, cols = 12 }) {
  const ref = useRef(null);

  const grid = useMemo(() => {
    return Array.from({ length: rows }, (_, r) =>
      Array.from({ length: cols }, (_, c) => {
        const seed = r * 31 + c * 7 + 1;
        const colWeight = Math.max(0, 1 - Math.abs(c - 3) / (cols - 1)); // peak around col 2
        const rowWeight = 0.55 + 0.45 * Math.sin((r / rows) * Math.PI);
        const o = Math.min(0.95, 0.06 + colWeight * rowWeight * (0.45 + rand(seed) * 0.55));
        return {
          o,
          weight: colWeight * rowWeight,
          delay: (Math.random() * 9).toFixed(2),
          dur: (7 + Math.random() * 7).toFixed(2),
          pos: Math.round((c / cols) * 100),
        };
      })
    );
  }, [rows, cols]);

  useEffect(() => {
    const root = ref.current;
    if (!root || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;

    // Candidate cells, weighted toward the brighter part of the field.
    const cells = [...root.querySelectorAll('.of-hero-cell')];
    const pool = [];
    cells.forEach((el, i) => {
      const w = grid[Math.floor(i / cols)][i % cols].weight;
      const copies = w > 0.55 ? 3 : w > 0.3 ? 2 : w > 0.12 ? 1 : 0;
      for (let k = 0; k < copies; k += 1) pool.push(el);
    });
    if (!pool.length) return undefined;

    let visible = true;
    const io = 'IntersectionObserver' in window
      ? new IntersectionObserver(([e]) => { visible = e.isIntersecting; })
      : null;
    io?.observe(root);

    const timers = new Set();
    const spark = () => {
      if (!visible || document.hidden) return;
      const burst = Math.random() < 0.7 ? 1 : 2;
      for (let n = 0; n < burst; n += 1) {
        const el = pool[Math.floor(Math.random() * pool.length)];
        if (el.classList.contains('is-white')) continue;
        el.classList.add('is-white');
        const t = setTimeout(() => {
          el.classList.remove('is-white');
          timers.delete(t);
        }, 2600 + Math.random() * 2600); // stays lit a moment after fully fading in
        timers.add(t);
      }
    };

    let loop;
    const schedule = () => {
      loop = setTimeout(() => {
        spark();
        schedule();
      }, 450 + Math.random() * 900);
    };
    schedule();

    return () => {
      clearTimeout(loop);
      timers.forEach(clearTimeout);
      io?.disconnect();
    };
  }, [grid, cols]);

  const template = `repeat(${cols}, minmax(0, 1fr))`;

  return (
    <div ref={ref} className="of-hero-matrix" aria-hidden="true">
      {grid.map((row, r) => (
        <div key={r} className="of-hero-row" style={{ gridTemplateColumns: template }}>
          {row.map((cell, c) => (
            <span
              key={c}
              className="of-hero-cell"
              style={{
                '--o': cell.o,
                '--delay': `-${cell.delay}s`,
                '--dur': `${cell.dur}s`,
                '--pos': `${cell.pos}% 0`,
              }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
